import React, { lazy } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { RequireRole } from '@/components/auth/RequireRole'
import { PlaceholderPage } from '@/pages/public/PlaceholderPage'
import { PageSkeleton } from '@/components/common/PageSkeleton'

/* ------------------------------------------------------------------ */
/*  router.tsx — full route tree                                       */
/*  See ARCHITECTURE.md §6 for all routes                               */
/*  Lazy imports for code-splitting; AppShell is the root layout        */
/* ------------------------------------------------------------------ */

// ─── Auth pages (eager — small) ─────────────────────────────────────
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const SignupPage = lazy(() => import('@/pages/auth/SignupPage'))
const MagicLinkSentPage = lazy(() => import('@/pages/auth/MagicLinkSentPage'))
const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage'))
const BankIdCallbackPage = lazy(() => import('@/pages/auth/BankIdCallbackPage'))

// ─── Error boundary ─────────────────────────────────────────────────
const RouteErrorBoundary = lazy(() => import('@/components/common/RouteErrorBoundary'))

// ─── Public pages ───────────────────────────────────────────────────
const LandingPage = lazy(() => import('@/pages/public/LandingPage'))
const CategoryIndexPage = lazy(() => import('@/pages/public/CategoryIndexPage'))
const CategoryPage = lazy(() => import('@/pages/public/CategoryPage'))
const CityCategoryPage = lazy(() => import('@/pages/public/CityCategoryPage'))
const CitiesIndexPage = lazy(() => import('@/pages/public/CitiesIndexPage'))
const CityPage = lazy(() => import('@/pages/public/CityPage'))
const ProviderProfilePage = lazy(() => import('@/pages/public/ProviderProfilePage'))
const HowItWorksPage = lazy(() => import('@/pages/public/HowItWorksPage'))
const PricingPage = lazy(() => import('@/pages/public/PricingPage'))
const ContactPage = lazy(() => import('@/pages/public/ContactPage'))
const NotFoundPage = lazy(() => import('@/pages/public/NotFoundPage'))
const SearchPage = lazy(() => import('@/pages/public/SearchPage'))
const ForProvidersPage = lazy(() => import('@/pages/public/ForProvidersPage'))
const AboutPage = lazy(() => import('@/pages/public/AboutPage'))
const FaqPage = lazy(() => import('@/pages/public/FaqPage'))
const PressPage = lazy(() => import('@/pages/public/PressPage'))
const RutCalculatorPage = lazy(() => import('@/pages/public/RutCalculatorPage'))
const TermsPage = lazy(() => import('@/pages/public/TermsPage'))
const PrivacyPage = lazy(() => import('@/pages/public/PrivacyPage'))
const SecurityPage = lazy(() => import('@/pages/public/SecurityPage'))

// ─── Internal dev pages ─────────────────────────────────────────────
const DesignSystemPage = lazy(() => import('@/pages/internal/DesignSystemPage'))

// ─── Admin pages ────────────────────────────────────────────────────
const DevInboxPage = lazy(() => import('@/pages/admin/DevInboxPage'))
const ServicesHealthPage = lazy(() => import('@/pages/admin/ServicesHealthPage'))

// ─── Customer pages ─────────────────────────────────────────────────
const CustomerDashboardPage = lazy(() => import('@/pages/customer/DashboardPage'))
const RequestNewPage = lazy(() => import('@/pages/customer/RequestNewPage'))
const BookingConfirmationPage = lazy(() => import('@/pages/customer/BookingConfirmationPage'))
const BookingsListPage = lazy(() => import('@/pages/customer/BookingsListPage'))
const FavoritesPage = lazy(() => import('@/pages/customer/FavoritesPage'))
const BookingDetailPage = lazy(() => import('@/pages/customer/BookingDetailPage'))
const ReviewPage = lazy(() => import('@/pages/customer/ReviewPage'))
const SettingsPage = lazy(() => import('@/pages/customer/SettingsPage'))

// ─── Provider pages ─────────────────────────────────────────────────
const ProviderInboxPage = lazy(() => import('@/pages/provider/InboxPage'))
const ProviderJobDetailPage = lazy(() => import('@/pages/provider/JobDetailPage'))
const ProviderDashboardPage = lazy(() => import('@/pages/provider/DashboardPage'))
const OnboardingPage = lazy(() => import('@/pages/provider/OnboardingPage'))
const ProviderCalendarPage = lazy(() => import('@/pages/provider/CalendarPage'))
const ProviderBookingDetailPage = lazy(() => import('@/pages/provider/BookingDetailPage'))
const ProviderProfilePage2 = lazy(() => import('@/pages/provider/ProfilePage'))
const ProviderDocumentsPage = lazy(() => import('@/pages/provider/DocumentsPage'))
const ProviderEarningsPage = lazy(() => import('@/pages/provider/EarningsPage'))
const ProviderSettingsPage = lazy(() => import('@/pages/provider/SettingsPage'))
const ProviderNotificationsPage = lazy(() => import('@/pages/provider/NotificationsPage'))
const ProviderStatistikPage = lazy(() => import('@/pages/provider/StatistikPage'))
const ProviderBokningarPage = lazy(() => import('@/pages/provider/BokningarPage'))
const ProviderRecensionerPage = lazy(() => import('@/pages/provider/RecensionerPage'))
const QuoteComposer = lazy(() => import('@/components/provider/QuoteComposer'))
const UpgradePage = lazy(() => import('@/pages/provider/UpgradePage'))

// ─── Dev pages (mock flows) ────────────────────────────────────────
const BankIdPage = lazy(() => import('@/pages/dev/BankIdPage'))
const StripeOnboardPage = lazy(() => import('@/pages/dev/StripeOnboardPage'))

// ─── Public placeholder pages (replaced in Phase 2+) ───────────────
function ph(title: string, path: string) {
  return <PlaceholderPage title={title} path={path} />
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    errorElement: <React.Suspense fallback={<PageSkeleton />}><RouteErrorBoundary /></React.Suspense>,
    children: [
      // ═══════════════════════════════════════════════════════════════
      // PUBLIC ROUTES
      // ═══════════════════════════════════════════════════════════════
      { index: true, element: <React.Suspense fallback={<PageSkeleton />}><LandingPage /></React.Suspense> },
      { path: 'tjanster', element: <React.Suspense fallback={<PageSkeleton />}><CategoryIndexPage /></React.Suspense> },
      { path: 'tjanster/:slug', element: <React.Suspense fallback={<PageSkeleton />}><CategoryPage /></React.Suspense> },
      { path: 'tjanster/:slug/:city', element: <React.Suspense fallback={<PageSkeleton />}><CityCategoryPage /></React.Suspense> },
      { path: 'stader', element: <React.Suspense fallback={<PageSkeleton />}><CitiesIndexPage /></React.Suspense> },
      { path: 'stader/:city', element: <React.Suspense fallback={<PageSkeleton />}><CityPage /></React.Suspense> },
      { path: 'leverantor/:slug', element: <React.Suspense fallback={<PageSkeleton />}><ProviderProfilePage /></React.Suspense> },
      { path: 'hur-det-fungerar', element: <React.Suspense fallback={<PageSkeleton />}><HowItWorksPage /></React.Suspense> },
      { path: 'priser', element: <React.Suspense fallback={<PageSkeleton />}><PricingPage /></React.Suspense> },
      { path: 'rut-kalkylator', element: <React.Suspense fallback={<PageSkeleton />}><RutCalculatorPage /></React.Suspense> },
      { path: 'for-leverantorer', element: <React.Suspense fallback={<PageSkeleton />}><ForProvidersPage /></React.Suspense> },
      { path: 'om-oss', element: <React.Suspense fallback={<PageSkeleton />}><AboutPage /></React.Suspense> },
      { path: 'blogg', element: ph('Blogg', '/blogg') },
      { path: 'blogg/:slug', element: ph('Blogginlägg', '/blogg/:slug') },
      { path: 'faq', element: <React.Suspense fallback={<PageSkeleton />}><FaqPage /></React.Suspense> },
      { path: 'kontakt', element: <React.Suspense fallback={<PageSkeleton />}><ContactPage /></React.Suspense> },
      { path: 'villkor', element: <React.Suspense fallback={<PageSkeleton />}><TermsPage /></React.Suspense> },
      { path: 'integritet', element: <React.Suspense fallback={<PageSkeleton />}><PrivacyPage /></React.Suspense> },
      { path: 'sakerhet', element: <React.Suspense fallback={<PageSkeleton />}><SecurityPage /></React.Suspense> },
      { path: 'press', element: <React.Suspense fallback={<PageSkeleton />}><PressPage /></React.Suspense> },
      { path: 'sok', element: <React.Suspense fallback={<PageSkeleton />}><SearchPage /></React.Suspense> },
      { path: 'leverantorer', element: ph('Leverantörer', '/leverantorer') },
      { path: 'skapa-forfragan', element: <React.Suspense fallback={<PageSkeleton />}><RequestNewPage /></React.Suspense> },

      // ═══════════════════════════════════════════════════════════════
      // AUTH ROUTES (unguarded)
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'logga-in',
        element: (
          <React.Suspense fallback={<PageSkeleton />}>
            <LoginPage />
          </React.Suspense>
        ),
      },
      {
        path: 'skapa-konto',
        element: (
          <React.Suspense fallback={<PageSkeleton />}>
            <SignupPage />
          </React.Suspense>
        ),
      },
      {
        path: 'magisk-lank-skickad',
        element: (
          <React.Suspense fallback={<PageSkeleton />}>
            <MagicLinkSentPage />
          </React.Suspense>
        ),
      },
      {
        path: 'auth/callback',
        element: (
          <React.Suspense fallback={<PageSkeleton />}>
            <AuthCallbackPage />
          </React.Suspense>
        ),
      },
      {
        path: 'auth/bankid-callback',
        element: (
          <React.Suspense fallback={<PageSkeleton />}>
            <BankIdCallbackPage />
          </React.Suspense>
        ),
      },

      // ═══════════════════════════════════════════════════════════════
      // CUSTOMER ROUTES (guarded: RequireAuth + RequireRole customer)
      // ═══════════════════════════════════════════════════════════════
      {
        element: <RequireAuth />,
        children: [
          {
            element: <RequireRole role="customer" />,
            children: [
              { path: 'dashboard', element: <React.Suspense fallback={<PageSkeleton />}><CustomerDashboardPage /></React.Suspense> },
              { path: 'dashboard/ny-fraga', element: <React.Suspense fallback={<PageSkeleton />}><RequestNewPage /></React.Suspense> },
              { path: 'dashboard/fraga/:id', element: ph('Förfrågan', '/dashboard/fraga/:id') },
              { path: 'dashboard/bokningar', element: <React.Suspense fallback={<PageSkeleton />}><BookingsListPage /></React.Suspense> },
              { path: 'dashboard/favoriter', element: <React.Suspense fallback={<PageSkeleton />}><FavoritesPage /></React.Suspense> },
              { path: 'dashboard/bokning/:id', element: <React.Suspense fallback={<PageSkeleton />}><BookingDetailPage /></React.Suspense> },
              {
                path: 'dashboard/bokning/:id/bekraftelse',
                element: <React.Suspense fallback={<PageSkeleton />}><BookingConfirmationPage /></React.Suspense>,
              },
              {
                path: 'dashboard/bokning/:id/omdome',
                element: <React.Suspense fallback={<PageSkeleton />}><ReviewPage /></React.Suspense>,
              },
              {
                path: 'dashboard/installningar',
                element: <React.Suspense fallback={<PageSkeleton />}><SettingsPage /></React.Suspense>,
              },
            ],
          },
        ],
      },

      // ═══════════════════════════════════════════════════════════════
      // PROVIDER ROUTES (guarded: RequireAuth + RequireRole provider)
      // ═══════════════════════════════════════════════════════════════
      {
        element: <RequireAuth />,
        children: [
          {
            element: <RequireRole role="provider" />,
            children: [
              {
                path: 'leverantor-portal',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderDashboardPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/registrering',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <OnboardingPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/inkorg',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderInboxPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/forfragan/:id',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderJobDetailPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/forfragan/:id/offert',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <QuoteComposer />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/kalender',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderCalendarPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/bokningar',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderBokningarPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/bokning/:id',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderBookingDetailPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/profil',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderProfilePage2 />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/dokument',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderDocumentsPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/intakter',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderEarningsPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/intakter/uppgradera',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <UpgradePage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/installningar',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderSettingsPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/notiser',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderNotificationsPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/statistik',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderStatistikPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'leverantor-portal/recensioner',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ProviderRecensionerPage />
                  </React.Suspense>
                ),
              },
            ],
          },
        ],
      },

      // ═══════════════════════════════════════════════════════════════
      // ADMIN ROUTES (guarded: RequireAuth + RequireRole admin + BankID)
      // ═══════════════════════════════════════════════════════════════
      {
        element: <RequireAuth />,
        children: [
          {
            element: <RequireRole role="admin" requireBankId />,
            children: [
              { path: 'admin', element: ph('Admin', '/admin') },
              { path: 'admin/users', element: ph('Användare', '/admin/users') },
              { path: 'admin/providers', element: ph('Leverantörer', '/admin/providers') },
              { path: 'admin/categories', element: ph('Kategorier', '/admin/categories') },
              { path: 'admin/bookings', element: ph('Bokningar', '/admin/bookings') },
              { path: 'admin/payments', element: ph('Betalningar', '/admin/payments') },
              { path: 'admin/content', element: ph('Innehåll', '/admin/content') },
              { path: 'admin/settings', element: ph('Inställningar', '/admin/settings') },
              {
                path: 'admin/dev-inbox',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <DevInboxPage />
                  </React.Suspense>
                ),
              },
              {
                path: 'admin/services-health',
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <ServicesHealthPage />
                  </React.Suspense>
                ),
              },
            ],
          },
        ],
      },

      // ═══════════════════════════════════════════════════════════════
      // DEV MOCK ROUTES (BankID, Stripe onboard, etc.)
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'dev/bankid',
        element: (
          <React.Suspense fallback={<PageSkeleton />}>
            <BankIdPage />
          </React.Suspense>
        ),
      },
      {
        path: 'dev/stripe-onboard/:id',
        element: (
          <React.Suspense fallback={<PageSkeleton />}>
            <StripeOnboardPage />
          </React.Suspense>
        ),
      },

      // ═══════════════════════════════════════════════════════════════
      // INTERNAL DEV ROUTES (hidden in prod)
      // ═══════════════════════════════════════════════════════════════
      ...(import.meta.env.DEV
        ? [
            {
              path: 'internal/design-system',
              element: (
                <React.Suspense fallback={<PageSkeleton />}>
                  <DesignSystemPage />
                </React.Suspense>
              ),
            },
          ]
        : []),

      // ═══════════════════════════════════════════════════════════════
      // CATCH-ALL — 404
      // ═══════════════════════════════════════════════════════════════
      { path: '*', element: <React.Suspense fallback={<PageSkeleton />}><NotFoundPage /></React.Suspense> },
    ],
  },
], { future: { v7_relativeSplatPath: true } })
